export const breakpoints = {
  mobile: 320,
  tablet: 768,
  desktop: 1024,
  xl: 1400,
};

export const getResponsiveValue = (mobile: any, tablet?: any, desktop?: any, xl?: any) => {
  return { mobile, tablet: tablet ?? mobile, desktop: desktop ?? tablet ?? mobile, xl: xl ?? desktop ?? tablet ?? mobile };
};

export const responsiveSpacing = {
  xs: getResponsiveValue('4px'),
  sm: getResponsiveValue('8px'),
  md: getResponsiveValue('12px'),
  lg: getResponsiveValue('12px', '16px'),
  xl: getResponsiveValue('16px', '24px'),
  '2xl': getResponsiveValue('24px', '32px'),
  '3xl': getResponsiveValue('32px', '48px'),
};

export const responsiveFontSize = {
  xs: getResponsiveValue('10px', '11px'),
  sm: getResponsiveValue('11px', '12px'),
  base: getResponsiveValue('12px', '14px'),
  md: getResponsiveValue('13px', '15px'),
  lg: getResponsiveValue('14px', '16px'),
  xl: getResponsiveValue('16px', '18px'),
  '2xl': getResponsiveValue('18px', '20px'),
  '3xl': getResponsiveValue('20px', '24px'),
  '4xl': getResponsiveValue('22px', '28px'),
  '5xl': getResponsiveValue('28px', '32px'),
  '6xl': getResponsiveValue('32px', '36px'),
  '7xl': getResponsiveValue('40px', '48px'),
};

export const getResponsiveStyle = (breakpoint: keyof typeof breakpoints, values: any) => {
  const bp = breakpoints[breakpoint];
  return `@media (min-width: ${bp}px) { ${values} }`;
};

export const dpsTheme = {
  colors: {
    background: {
      primary: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)',
      card: 'rgba(30, 41, 59, 0.4)',
      cardHover: 'rgba(30, 41, 59, 0.6)',
      dark: 'rgba(15, 23, 42, 0.4)',
      darker: 'rgba(15, 23, 42, 0.6)',
      header: 'rgba(15, 23, 42, 0.8)',
    },
    border: {
      primary: 'rgba(255, 255, 255, 0.1)',
      secondary: 'rgba(255, 255, 255, 0.05)',
      hover: 'rgba(255, 255, 255, 0.15)',
    },
    categories: {
      protection: {
        primary: '#3b82f6',
        secondary: '#60a5fa',
        bg: 'rgba(59, 130, 246, 0.15)',
        border: 'rgba(59, 130, 246, 0.3)',
        gradient: 'linear-gradient(90deg, #3b82f6, #60a5fa)',
      },
      surveillance: {
        primary: '#8b5cf6',
        secondary: '#a78bfa',
        bg: 'rgba(139, 92, 246, 0.15)',
        border: 'rgba(139, 92, 246, 0.3)',
        gradient: 'linear-gradient(90deg, #8b5cf6, #a78bfa)',
      },
      dissuasion: {
        primary: '#ec4899',
        secondary: '#f472b6',
        bg: 'rgba(236, 72, 153, 0.15)',
        border: 'rgba(236, 72, 153, 0.3)',
        gradient: 'linear-gradient(90deg, #ec4899, #f472b6)',
      },
    },
    priority: {
      high: {
        bg: 'rgba(239, 68, 68, 0.15)',
        border: 'rgba(239, 68, 68, 0.3)',
        text: '#f87171',
      },
      medium: {
        bg: 'rgba(245, 158, 11, 0.15)',
        border: 'rgba(245, 158, 11, 0.3)',
        text: '#fbbf24',
      },
      low: {
        bg: 'rgba(59, 130, 246, 0.15)',
        border: 'rgba(59, 130, 246, 0.3)',
        text: '#60a5fa',
      },
    },
    status: {
      completed: {
        bg: 'rgba(16, 185, 129, 0.15)',
        border: 'rgba(16, 185, 129, 0.3)',
        text: '#6ee7b7',
      },
      planned: {
        bg: 'rgba(59, 130, 246, 0.15)',
        border: 'rgba(59, 130, 246, 0.3)',
        text: '#60a5fa',
      },
      notPlanned: {
        bg: 'rgba(100, 116, 139, 0.15)',
        border: 'rgba(100, 116, 139, 0.3)',
        text: '#94a3b8',
      },
    },
    scoreLevel: {
      excellent: {
        primary: '#10b981',
        secondary: '#34d399',
        bg: 'rgba(16, 185, 129, 0.15)',
        border: 'rgba(16, 185, 129, 0.3)',
        text: '#6ee7b7',
        gradient: 'linear-gradient(90deg, #10b981, #34d399)',
      },
      good: {
        primary: '#f59e0b',
        secondary: '#fbbf24',
        bg: 'rgba(245, 158, 11, 0.15)',
        border: 'rgba(245, 158, 11, 0.3)',
        text: '#fbbf24',
        gradient: 'linear-gradient(90deg, #f59e0b, #fbbf24)',
      },
      moderate: {
        primary: '#fbbf24',
        secondary: '#f59e0b',
        bg: 'rgba(251, 191, 36, 0.15)',
        border: 'rgba(251, 191, 36, 0.3)',
        text: '#fbbf24',
        gradient: 'linear-gradient(90deg, #fbbf24, #f59e0b)',
      },
      weak: {
        primary: '#ef4444',
        secondary: '#f87171',
        bg: 'rgba(239, 68, 68, 0.15)',
        border: 'rgba(239, 68, 68, 0.3)',
        text: '#fca5a5',
        gradient: 'linear-gradient(90deg, #ef4444, #f87171)',
      },
    },
    text: {
      primary: '#f8fafc',
      secondary: '#e2e8f0',
      tertiary: '#cbd5e1',
      muted: '#94a3b8',
      disabled: '#64748b',
    },
    accent: {
      blue: {
        primary: '#3b82f6',
        secondary: '#2563eb',
        gradient: 'linear-gradient(135deg, #3b82f6, #2563eb)',
        bg: 'rgba(59, 130, 246, 0.15)',
        border: 'rgba(59, 130, 246, 0.3)',
      },
      green: {
        primary: '#10b981',
        secondary: '#34d399',
        gradient: 'linear-gradient(135deg, #10b981, #34d399)',
        bg: 'rgba(16, 185, 129, 0.15)',        // ← Ajoute
        border: 'rgba(16, 185, 129, 0.3)',     // ← Ajoute
      },
      purple: {
        primary: '#8b5cf6',
        secondary: '#ec4899',
        gradient: 'linear-gradient(135deg, #8b5cf6, #ec4899)',
      },
    },
  },
  shadows: {
    card: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    hover: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    button: '0 4px 12px rgba(59, 130, 246, 0.4)',
  },
  borderRadius: {
    sm: '8px',
    md: '12px',
    lg: '16px',
    xl: '20px',
    full: '9999px',
  },
  spacing: {
    xs: '4px',
    sm: '8px',
    md: '12px',
    lg: '16px',
    xl: '24px',
    '2xl': '32px',
    '3xl': '48px',
  },
  typography: {
    fontFamily: "'Inter', sans-serif",
    fontSize: {
      xs: '11px',
      sm: '12px',
      base: '14px',
      md: '15px',
      lg: '16px',
      xl: '18px',
      '2xl': '20px',
      '3xl': '24px',
      '4xl': '28px',
      '5xl': '32px',
      '6xl': '36px',
      '7xl': '48px',
    },
    fontWeight: {
      light: 300,
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
    lineHeight: {
      tight: 1.2,
      normal: 1.5,
      relaxed: 1.6,
      loose: 1.8,
    },
  },
};

export const getScoreLevelConfig = (score: number) => {
  if (score >= 80) return { ...dpsTheme.colors.scoreLevel.excellent, label: 'Excellent niveau' };
  if (score >= 60) return { ...dpsTheme.colors.scoreLevel.good, label: 'Bon niveau' };
  if (score >= 40) return { ...dpsTheme.colors.scoreLevel.moderate, label: 'Niveau modéré' };
  return { ...dpsTheme.colors.scoreLevel.weak, label: 'Niveau faible' };
};

export const getCategoryIcon = (category: string) => {
  const icons: Record<string, string> = {
    protection: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z',
    surveillance: 'M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z M12 13a4 4 0 1 0 0-8 4 4 0 0 0 0 8z',
    dissuasion: 'M13 2L3 14h9l-1 8 10-12h-9l1-8z',
  };
  return icons[category.toLowerCase()] || icons.protection;
};

export const getPriorityLabel = (priority: 'high' | 'medium' | 'low') => {
  const labels = {
    high: 'Haute',
    medium: 'Moyenne',
    low: 'Basse',
  };
  return labels[priority];
};

export const getStatusLabel = (status: string) => {
  if (status === 'not_planned') return 'Suggéré';
  if (status === 'completed') return 'Réalisée';
  if (status === 'planned') return 'Planifiée';
  if (status === 'in_progress') return 'En cours';
  if (status === 'cancelled') return 'Annulée';
  return 'Suggéré';
};
